'use client';

import { useEffect } from 'react';

const COOKIE_NAME = '_medusa_cache_id';
const MAX_AGE_SECONDS = 60 * 60 * 24;
// Refresh well before the TTL so a continuously-visible tab (where neither
// visibilitychange nor pageshow ever fires) cannot outlive the cookie.
const REFRESH_INTERVAL_MS = (MAX_AGE_SECONDS / 4) * 1000;

/**
 * (Re)issue the per-user cache-busting cookie, preserving an existing id.
 *
 * Re-setting an existing id refreshes its expiry, so the cookie cannot
 * lapse mid-session while the tab stays active.
 */
const ensureCacheIdCookie = () => {
  const existing = document.cookie
    .split('; ')
    .find((cookie) => cookie.startsWith(`${COOKIE_NAME}=`))
    ?.slice(COOKIE_NAME.length + 1);

  const cacheId = existing || crypto.randomUUID();

  if (typeof cookieStore !== 'undefined') {
    cookieStore
      .set({
        name: COOKIE_NAME,
        value: cacheId,
        expires: Date.now() + MAX_AGE_SECONDS * 1000,
        sameSite: 'lax',
        path: '/',
      })
      .catch(() => {
        // Async failure — fail silently; the next request renders uncached.
      });
  } else {
    // Safari < 18.4 and older iOS don't have the Cookie Store API.
    // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API fallback for browsers without it
    document.cookie = `${COOKIE_NAME}=${cacheId}; max-age=${MAX_AGE_SECONDS}; path=/; samesite=lax`;
  }
};

/**
 * Keeps the per-user cache-busting cookie (`_medusa_cache_id`) alive on the
 * client.
 *
 * The proxy used to set it on responses that arrived without the cookie, but
 * that `Set-Cookie` header made otherwise-cacheable HTML ineligible for
 * Cloudflare's edge cache (and an edge-cached `Set-Cookie` would hand one
 * user's id to everyone). Issuing it after hydration keeps the cache-id
 * cookie out of document responses; a request without the cookie falls back
 * to uncached data fetching, which was already the behaviour for every
 * visitor's first request. (The proxy still sets `_pricing_channel` on PPC
 * landings — those carry ad query params and are excluded from edge caching.)
 *
 * The cookie is checked on mount, re-checked whenever the tab regains
 * visibility (or is restored from the bfcache), and refreshed on an interval
 * at a quarter of the TTL — the interval covers tabs that stay continuously
 * visible, while the listeners cover background tabs whose timers the
 * browser throttles. The root layout mounts once per browser session, so a
 * mount-only effect would let the 24-hour cookie lapse mid-session and
 * silently disable per-user caching until a full reload.
 *
 * The cookie is strictly necessary (per-user Data Cache tags only) and must
 * not be repurposed for analytics or tracking (PECR compliance).
 */
export default function CacheIdInitializer() {
  useEffect(() => {
    ensureCacheIdCookie();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        ensureCacheIdCookie();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', ensureCacheIdCookie);
    const refreshTimer = setInterval(ensureCacheIdCookie, REFRESH_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', ensureCacheIdCookie);
      clearInterval(refreshTimer);
    };
  }, []);

  return null;
}
